import { z } from 'zod';
import { FeatureGroup } from './house.js';
/** House-wide privileges that are not tied to a single room. */
export const GlobalPrivilege = z.enum([
    'alarm_view',
    'alarm_disarm',
    'alarm_arm',
    'front_door_unlock',
    'wifi',
    'house_info',
]);
export const GLOBAL_PRIVILEGES = GlobalPrivilege.options;
export const GLOBAL_PRIVILEGE_LABELS = {
    alarm_view: 'See alarm status',
    alarm_disarm: 'Disarm the alarm',
    alarm_arm: 'Arm the alarm',
    front_door_unlock: 'Unlock the front door',
    wifi: 'See the wifi password',
    house_info: 'House-sitting notes',
};
/**
 * How much of the house's lighting schedule this guest controls.
 *  none  - they see nothing about timings
 *  room  - they set wake/bed times for the room they are staying in
 *  house - house-sitting: they set the times for the whole house
 */
export const ScheduleScope = z.enum(['none', 'room', 'house']);
export const SCHEDULE_SCOPE_LABELS = {
    none: 'No control over timings',
    room: 'Their room only',
    house: 'The whole house (house-sitting)',
};
export const RoomGrant = z.object({
    roomId: z.string(),
    groups: z.array(FeatureGroup),
});
export const Permissions = z.object({
    globals: z.array(GlobalPrivilege),
    rooms: z.array(RoomGrant),
});
export const EMPTY_PERMISSIONS = { globals: [], rooms: [] };
export const Invite = z.object({
    id: z.string(),
    /** Human-readable redemption code, e.g. WOOD-4821. Stored uppercased. */
    code: z.string(),
    guestName: z.string(),
    /** The room this guest is staying in; drives the "Your room" home card. */
    guestRoomId: z.string().nullable(),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    permissions: Permissions,
    scheduleScope: ScheduleScope.default('none'),
    /** House-sitters exclude the master suite unless this is ticked. */
    scheduleIncludesMaster: z.boolean().default(false),
    note: z.string(),
    revokedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    lastSeenAt: z.iso.datetime().nullable(),
    sessionCount: z.number().int().nonnegative(),
});
export const InviteStatus = z.enum(['scheduled', 'active', 'expired', 'revoked']);
export function inviteStatus(invite, now = new Date()) {
    if (invite.revokedAt !== null)
        return 'revoked';
    const t = now.getTime();
    if (t < Date.parse(invite.startsAt))
        return 'scheduled';
    if (t > Date.parse(invite.endsAt))
        return 'expired';
    return 'active';
}
export const CreateInviteInput = z.object({
    guestName: z.string().min(1).max(80),
    guestRoomId: z.string().nullable(),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    permissions: Permissions,
    scheduleScope: ScheduleScope.default('none'),
    scheduleIncludesMaster: z.boolean().default(false),
    note: z.string().max(500).default(''),
});
/**
 * Spelled out rather than `CreateInviteInput.partial()`: partial() leaves the
 * field defaults in place, so a PATCH that omitted `scheduleScope` silently
 * reset it to 'none' — quietly stripping a guest's lighting control — and
 * likewise blanked `note`. Every field here is optional with no default, so an
 * omitted key means "leave it alone".
 */
export const UpdateInviteInput = z.object({
    guestName: z.string().min(1).max(80).optional(),
    guestRoomId: z.string().nullable().optional(),
    startsAt: z.iso.datetime().optional(),
    endsAt: z.iso.datetime().optional(),
    permissions: Permissions.optional(),
    scheduleScope: ScheduleScope.optional(),
    scheduleIncludesMaster: z.boolean().optional(),
    note: z.string().max(500).optional(),
});
//# sourceMappingURL=invite.js.map